/**
 * DaumPostcodeModal — 주소 검색 (임베드 방식)
 *
 * ⚠️ iOS WebView(WKWebView) 대응:
 *   daum.Postcode 의 .open() 은 새 창(window.open)을 띄우는데,
 *   iOS/iPad WebView 는 새 창을 막아서 빈 화면이 됨 (App Store 리젝 사유).
 *   → .embed() 로 모달 div 안에 인라인 렌더하여 새 창 없이 동작.
 *
 * 사용:
 *   {open && (
 *     <DaumPostcodeModal
 *       onClose={() => setOpen(false)}
 *       onComplete={(addr) => set('location', addr)}
 *     />
 *   )}
 */
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';

const POSTCODE_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

let loadingPromise = null;
// daum.Postcode 스크립트가 아직 안 떴으면 동적 로드 (index.html 로드 실패 폴백)
function ensurePostcode() {
  if (typeof window !== 'undefined' && window.daum?.Postcode) return Promise.resolve();
  if (loadingPromise) return loadingPromise;
  loadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = POSTCODE_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { loadingPromise = null; reject(new Error('postcode load failed')); };
    document.head.appendChild(s);
  });
  return loadingPromise;
}

const DaumPostcodeModal = ({ onClose, onComplete }) => {
  const boxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    ensurePostcode()
      .then(() => {
        if (cancelled || !boxRef.current || !window.daum?.Postcode) return;
        new window.daum.Postcode({
          oncomplete: (d) => {
            const addr = d.roadAddress || d.jibunAddress || d.address || '';
            onComplete?.(addr);
            onClose?.();
          },
          onresize: (size) => {
            if (boxRef.current) boxRef.current.style.height = `${size.height}px`;
          },
          width: '100%',
          height: '100%',
        }).embed(boxRef.current);
      })
      .catch(() => {
        if (cancelled) return;
        alert('주소 검색을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
        onClose?.();
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Head>
          <Title>주소 검색</Title>
          <Close type="button" onClick={onClose}><IoClose size={22} /></Close>
        </Head>
        <Box ref={boxRef} />
      </Sheet>
    </Overlay>
  );
};

export default DaumPostcodeModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-height: 86vh;
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.divider || '#eee'};
`;

const Title = styled.p`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#111'};
`;

const Close = styled.button`
  background: none;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#888'};
  display: flex;
`;

const Box = styled.div`
  width: 100%;
  height: 460px;
`;
